// filepath: /Users/ephraim/yesodcopilot/client/src/pages/group/ClassDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faMapMarkerAlt, 
  faBookOpen,
  faChalkboardTeacher
} from '@fortawesome/free-solid-svg-icons';
import { Share2 } from 'lucide-react';
import { useGroup } from '../../context/GroupContext';
import { useAuth } from '../../context/AuthContext';
import groupService from '../../api/groupService';
import PaymentLinksModal from '../../components/modals/PaymentLinksModal';
import AppDownloadModal from '../../components/AppDownloadModal';
import { renderLocationWithLinks } from '../../utils/linkUtils';
import { createShareLinks } from '../../config';
import '../../styles/group/ClassDetails.css'; // Reuse EventDetails styles for now

const ClassDetails = () => {
  // URL params
  const { id: groupId, classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { adminGroup, userGroups } = useGroup();
  const { currentUser } = useAuth();
  
  // Determine if this is being viewed from public community page
  // Always show public view when accessed via /class/ (singular) route
  const isPublicView = location.pathname.includes('/class/');
  
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showPublicRegisterModal, setShowPublicRegisterModal] = useState(false);
  
  // Handle image loading errors
  const [headerImageError, setHeaderImageError] = useState(false);

  // Direct fix for Supabase URLs with special characters
  const processImageUrl = (url) => {
    if (!url) return null;
    
    try {
      // Remove excessive logging
      // console.log('ClassDetails - Original image URL:', url);
      
      // First try to create a valid URL object to see if it's parseable
      try {
        new URL(url);
      } catch (e) {
        console.error('Invalid URL format:', url);
        return null;
      }
      
      // Extract the base domain and path
      const urlObj = new URL(url);
      const baseDomain = `${urlObj.protocol}//${urlObj.host}`;
      const pathParts = urlObj.pathname.split('/');
      
      // Process each part of the path to properly encode
      const processedPathParts = pathParts.map((part, index) => {
        // Don't encode the first empty part (from the leading slash)
        if (index === 0 && part === '') return '';
        
        // For the filename (last part), ensure it's properly encoded
        if (index === pathParts.length - 1) {
          try {
            // First try to decode it in case it's already encoded but with issues
            const decodedPart = decodeURIComponent(part);
            // Then properly encode it
            return encodeURIComponent(decodedPart);
          } catch (e) {
            // If decoding fails, just encode as is
            return encodeURIComponent(part);
          }
        }
        
        // For path segments, just ensure they're properly encoded
        return encodeURIComponent(part);
      });
      
      // Build the URL back
      const processedPath = processedPathParts.join('/');
      let processedUrl = `${baseDomain}${processedPath}`;
      
      // Add query parameters if they exist
      if (urlObj.search) {
        processedUrl += urlObj.search;
      }
      
      // Remove excessive logging
      // console.log('ClassDetails - Processed image URL:', processedUrl);
      
      // Add a cache buster to prevent browser caching issues
      const separator = processedUrl.includes('?') ? '&' : '?';
      return `${processedUrl}${separator}t=${Date.now()}`;
    } catch (error) {
      console.error('Error processing image URL:', error);
      return url;
    }
  };

  // Handle share class
  const handleShareClass = async (e) => {
    e.stopPropagation();
    const shareUrl = createShareLinks.class(classId);
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      showCopyToast();
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Show copy toast notification
  const showCopyToast = () => {
    const toast = document.createElement('div');
    toast.className = 'copy-toast';
    toast.textContent = 'Link copied to clipboard!';
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.remove();
    }, 3000);
  };

  // Fetch class details, enrollments, and user's enrollment status
  useEffect(() => {
    const fetchClassData = async () => {
      try {
        setLoading(true);
        // Fetch class details (publicly accessible)
        const response = await groupService.getClass(groupId, classId);
        setClassData(response.data);
        
        // Only fetch enrollment data if user is authenticated
        if (currentUser) {
          // Fetch enrollment status for the current user
          try {
            const enrollmentResponse = await groupService.getUserClassEnrollment(groupId, classId);
            console.log('User enrollment response:', enrollmentResponse);
            
            // Enrollment status checking removed - no longer needed for UI
          } catch (enrollErr) {
            console.error('Error fetching enrollment status:', enrollErr);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching class details:', err);
        setError('Failed to load class details. Please try again later.');
        setLoading(false);
      }
    };

    fetchClassData();
  }, [groupId, classId, currentUser]);

  // Show app download modal on page load (with a small delay to ensure page renders first)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Check if user hasn't dismissed the modal recently (within 24 hours)
      const lastDismissed = localStorage.getItem('appDownloadModalDismissed');
      const now = Date.now();
      const oneDayInMs = 24 * 60 * 60 * 1000;
      
      if (!lastDismissed || (now - parseInt(lastDismissed)) > oneDayInMs) {
        setShowDownloadModal(true);
      }
    }, 500); // Show after 500ms to let the page render

    return () => clearTimeout(timer);
  }, []);

  // Format date - e.g., "Thursday, March 26, 2023"
  const formatDate = (dateString) => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format time - e.g., "8:00 PM"
  const formatTime = (dateString) => {
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  // Format recurring class display
  const formatRecurringDisplay = (isRecurring, recurrencePattern, recurrenceDay, dateString) => {
    if (!isRecurring || !recurrencePattern || recurrencePattern === 'none') {
      return null; // Use normal date display
    }
    
    const time = formatTime(dateString);
    
    if (recurrencePattern === 'daily') {
      return { text: `Every Day at ${time}`, showTime: false };
    } else if (recurrencePattern === 'weekly' && recurrenceDay) {
      const days = {
        'monday': 'Monday',
        'tuesday': 'Tuesday', 
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      const dayName = days[recurrenceDay.toLowerCase()] || recurrenceDay;
      return { text: `Every ${dayName} at ${time}`, showTime: false };
    }
    
    return null; // Fallback to normal date display
  };

  // Go back to group page or admin dashboard based on where user came from
  const handleBack = () => {
    // Check if user came from admin dashboard or community calendar
    if (location.state?.fromAdmin) {
      navigate(`/admin/dashboard/classes`);
    } else if (location.state?.fromCalendar) {
      navigate(`/admin/dashboard/calendar`);
    } else {
      // Navigate back to Classes tab in public community page
      navigate(`/group/${groupId}?tab=classes`);
    }
  };



  // Open sponsor modal
  const openSponsorModal = () => {
    setShowSponsorModal(true);
  };

  // Close sponsor modal
  const handleCloseSponsorModal = () => {
    setShowSponsorModal(false);
  };



  return (
    <div className="class-details-page">
      {loading ? (
        <div className="loading-container">Loading class details...</div>
      ) : error ? (
        <div className="error-container">{error}</div>
      ) : classData ? (
        <>
          {/* Class header with cover image */}
          <div className="class-details-header">
            {classData.cover_image_url && !headerImageError ? (
              <img 
                className="class-header-img"
                src={processImageUrl(classData.cover_image_url)}
                alt={classData.title}
                onError={() => setHeaderImageError(true)}
              />
            ) : (
              <div 
                className="class-header-placeholder"
                style={{
                  backgroundColor: '#5669FF',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '4rem'
                }}
              >
                <FontAwesomeIcon icon={faBookOpen} />
              </div>
            )}
            
            <div className="class-header-overlay"></div>
          </div>
          

          
          {/* Class details content */}
          <div className="event-details-content">
            <h1 className="event-title">{classData.title}</h1>
            
            <div className="event-meta">
              {/* Date and time section */}
              <div className="event-date-time">
                <div className="event-icon-container">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
                <div className="event-info-container">
                  {(() => {
                    const recurringDisplay = formatRecurringDisplay(classData.is_recurring, classData.recurrence_pattern, classData.recurrence_day, classData.date);
                    if (recurringDisplay) {
                      return <div className="event-date">{recurringDisplay.text}</div>;
                    } else {
                      return (
                        <>
                          <div className="event-date">{formatDate(classData.date)}</div>
                          <div className="event-time">{formatTime(classData.date)}</div>
                        </>
                      );
                    }
                  })()}
                </div>
              </div>
              
              {/* Location section */}
              {classData.location && (
                <div className="event-location">
                  <div className="event-icon-container">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </div>
                  <div className="event-location-text">
                    {renderLocationWithLinks(classData.location)}
                  </div>
                </div>
              )}
              
              {/* Teacher section */}
              {classData.teacher && (
                <div className="event-location">
                  <div className="event-icon-container">
                    <FontAwesomeIcon icon={faChalkboardTeacher} />
                  </div>
                  <div className="event-location-text">
                    {classData.teacher}
                  </div>
                </div>
              )}
            </div>
            
            {/* Description section */}
            <div className="event-description-section">
              <h2 className="event-description-title">About</h2>
              <div className="event-description">
                {classData.description}
              </div>
            </div>
            
            {/* Recurring class info */}
            {classData.is_recurring && (
              <div className="event-description-section">
                <h2 className="event-description-title">Class Schedule</h2>
                <div className="event-description">
                  <p>
                    This is a recurring class that meets {classData.recurrence_pattern}
                    {classData.recurrence_day && classData.recurrence_pattern === 'weekly' && 
                      ` on ${classData.recurrence_day.charAt(0).toUpperCase() + classData.recurrence_day.slice(1)}s`}.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Sponsor button - only show if sponsorship is allowed */}
          {classData.allow_sponsorship && (
            <div className="sponsor-button-container">
              <button 
                className="sponsor-button-simple"
                onClick={openSponsorModal}
                aria-label="Dedicate to this class"
              >
                DEDICATE THIS CLASS
              </button>
            </div>
          )}
          
          {/* Sponsor modal */}
          <PaymentLinksModal
            isOpen={showSponsorModal}
            onClose={handleCloseSponsorModal}
            groupId={groupId}
            eventId={classId}
            eventTitle={classData.title}
            isClass={true}
          />
          
          {/* App Download modal */}
          <AppDownloadModal
            isOpen={showDownloadModal}
            onClose={() => {
              setShowDownloadModal(false);
              localStorage.setItem('appDownloadModalDismissed', Date.now().toString());
            }}
            contentType="class"
          />

          {/* App Download modal for public registration */}
          <AppDownloadModal
            isOpen={showPublicRegisterModal}
            onClose={() => setShowPublicRegisterModal(false)}
            title="Register via the Yesod App"
            message="To register for this class, please download the Yesod app."
          />

          {/* Fixed Register button at bottom for public view or non-authenticated users */}
          {(isPublicView || !currentUser) && (
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
              <div className="max-w-3xl mx-auto">
                <button
                  className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-lg font-bold rounded-xl transition-colors shadow-md"
                  onClick={() => setShowPublicRegisterModal(true)}
                >
                  Register
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="error-container">Class not found</div>
      )}
    </div>
  );
};

export default ClassDetails;